import React, { useState, useEffect, useCallback, useRef } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { customerAPI, supplierAPI, bulkAPI, paymentAPI, cdNoteAPI, settingsAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';

const fmtDate = d => formatDateDMY(d, '—');
const fmtNum = n => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const isNew = dateStr => dateStr && (Date.now() - new Date(dateStr).getTime()) < 30 * 24 * 60 * 60 * 1000;

const INDIA_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan',
  'Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  'Andaman and Nicobar Islands','Chandigarh','Dadra and Nagar Haveli and Daman & Diu',
  'Delhi','Jammu & Kashmir','Ladakh','Lakshadweep','Puducherry',
];

/* ================================================================
   DETAILS MODAL  (3 tabs: invoices/bills | ledger | edit+delete)
================================================================= */
const DetailsModal = ({ party, partyType, onClose, onSaved, onDeleted }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };

  const [tab, setTab] = useState('activity');
  const [activity, setActivity] = useState([]);
  const [actLoading, setActLoading] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => party.created_at ? party.created_at.split('T')[0] : '');
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [entryFilter, setEntryFilter] = useState('all');
  const [ledgerSearch, setLedgerSearch] = useState('');
  const [ledgerSending, setLedgerSending] = useState('');
  // Credit/Debit Notes state
  const [cdNotes, setCdNotes] = useState([]);
  const [cdLoading, setCdLoading] = useState(false);
  const [cdSaving, setCdSaving] = useState(false);
  const [cdFilter, setCdFilter] = useState('all');
  const [showCdForm, setShowCdForm] = useState(false);
  const [cdType, setCdType] = useState('credit');
  const [cdForm, setCdForm] = useState({ customer_name: party.name || '', invoice_id: '', reason: '', description: '', amount: '', note_date: new Date().toISOString().split('T')[0], payment_mode: 'Cash' });
  const [editForm, setEditForm] = useState(() => {
    if (partyType === 'customer') {
      const rawType = (party.customer_type || 'normal').toLowerCase();
      const custType = ['normal', 'business'].includes(rawType) ? rawType : 'normal';
      const rawState = party.state || 'Maharashtra';
      const normState = INDIA_STATES.find(s => s.toLowerCase() === rawState.toLowerCase()) || rawState;
      return {
        name: party.name || '', customer_type: custType,
        mobile: party.mobile || '', mobile2: party.mobile2 || '', mobile3: party.mobile3 || '',
        email: party.email || '', phone: party.phone || '',
        birthday: party.birthday ? party.birthday.split('T')[0] : '',
        anniversary: party.anniversary ? party.anniversary.split('T')[0] : '',
        address: party.address || '', city: party.city || '', area: party.area || '', pincode: party.pincode || '',
        state: normState, country: party.country || 'India',
        gstin: party.gstin || '', pan: party.pan || '',
        tags: party.tags || '', notes: party.notes || '',
        opening_balance: party.opening_balance ?? '',
        op_bal_type: party.op_bal_type || 'debit',
        contact_person: party.contact_person || '',
        contact_no: party.contact_no || '',
        document_type: party.document_type || '',
        document_no: party.document_no || '',
        doc_issue_date: party.doc_issue_date ? party.doc_issue_date.split('T')[0] : '',
        credit_allowed: party.credit_allowed ?? true,
        credit_limit: party.credit_limit ?? '',
        price_category: party.price_category || 'retail',
        gst_type: party.gst_type || '',
        gst_verified: party.gst_verified ?? false,
        legal_name: party.legal_name || '',
      };
    } else {
      return {
        name: party.name || '', contact_person: party.contact_person || '',
        phone: party.phone || '', email: party.email || '',
        address: party.address || '', city: party.city || '', area: party.area || '', pincode: party.pincode || '',
        state: party.state || '', country: party.country || 'India',
        gstin: party.gstin || '', pan: party.pan || '',
        opening_balance: party.opening_balance ?? 0,
        op_bal_type: party.op_bal_type || 'debit',
        tax_state: party.tax_state || '',
        contact_no: party.contact_no || '',
        bank_name: party.bank_name || '',
        bank_ifsc: party.bank_ifsc || '',
        bank_account: party.bank_account || '',
        status: party.status || 'active', notes: party.notes || '',
      };
    }
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);
  const [editDirty, setEditDirty] = useState(false);
  const setF = k => e => { setEditDirty(true); setEditForm(f => ({ ...f, [k]: e.target.value })); };
  const setFMobile = k => e => { setEditDirty(true); const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 10); setEditForm(f => ({ ...f, [k]: v })); };

  const handleClose = useCallback(() => {
    if (tab === 'edit' && editDirty && !window.confirm('Discard unsaved changes? All edits to this record will be lost.')) return;
    onClose();
  }, [tab, editDirty, onClose]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [handleClose]);

  useEffect(() => {
    setActLoading(true);
    const p = partyType === 'customer'
      ? customerAPI.invoices(party.id).then(r => r.data?.invoices || [])
      : supplierAPI.listBills(party.id, { page_size: 50 }).then(r => r.data?.bills || []);
    p.then(d => { setActivity(d); setActLoading(false); }).catch(() => setActLoading(false));
  }, [party.id, partyType]);

  useEffect(() => {
    if (tab === 'ledger') {
      setLedgerLoading(true);
      paymentAPI.getLedger(partyType, party.id)
        .then(r => { setLedger(r.data); })
        .catch(() => { setLedger({ entries: [], total_in: 0, total_out: 0, balance: 0 }); })
        .finally(() => setLedgerLoading(false));
    }
  }, [tab, partyType, party.id]);

  // Credit/Debit Notes handlers
  const loadCdNotes = useCallback(async () => {
    setCdLoading(true);
    try {
      const params = { party_type: partyType, party_id: party.id };
      if (cdFilter !== 'all') params.note_type = cdFilter;
      const res = await cdNoteAPI.list(params);
      setCdNotes(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch { setCdNotes([]); }
    setCdLoading(false);
  }, [partyType, party.id, cdFilter]);

  useEffect(() => { if (tab === 'ledger') loadCdNotes(); }, [tab, loadCdNotes]);

  const resetCdForm = () => setCdForm({ customer_name: party.name || '', invoice_id: '', reason: '', description: '', amount: '', note_date: new Date().toISOString().split('T')[0], payment_mode: 'Cash' });

  const handleCreateCdNote = async (e) => {
    e.preventDefault();
    const amt = parseFloat(cdForm.amount || 0);
    if (!amt || amt <= 0) { alert('Please enter a valid amount'); return; }
    setCdSaving(true);
    try {
      await cdNoteAPI.create({
        note_type: cdType,
        party_type: partyType,
        party_id: party.id,
        customer_id: partyType === 'customer' ? party.id : undefined,
        supplier_id: partyType === 'supplier' ? party.id : undefined,
        invoice_id: cdForm.invoice_id || null,
        customer_name: cdForm.customer_name || party.name,
        reason: cdForm.reason || 'Adjustment',
        description: cdForm.description || null,
        amount: amt,
        subtotal: amt,
        gst_total: 0,
        grand_total: amt,
        note_date: cdForm.note_date || new Date().toISOString().split('T')[0],
        payment_mode: cdForm.payment_mode || 'Cash',
      });
      alert('Credit/Debit note created successfully');
      setShowCdForm(false);
      resetCdForm();
      await Promise.all([loadCdNotes(), paymentAPI.getLedger(partyType, party.id).then(r => setLedger(r.data)).catch(() => {})]);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create note'); }
    setCdSaving(false);
  };

  const handleDeleteCdNote = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await cdNoteAPI.delete(id);
      await Promise.all([loadCdNotes(), paymentAPI.getLedger(partyType, party.id).then(r => setLedger(r.data)).catch(() => {})]);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete note'); }
  };

  const filteredEntries = (ledger?.entries || []).filter(e => {
    if (entryFilter !== 'all' && e.entry_type !== entryFilter) return false;
    if (dateFrom && e.entry_date < dateFrom) return false;
    if (dateTo && e.entry_date > dateTo) return false;
    if (ledgerSearch) {
      const q = ledgerSearch.toLowerCase();
      if (!(e.description || '').toLowerCase().includes(q) &&
          !(e.payment_mode || '').toLowerCase().includes(q) &&
          !(String(e.reference_id || '')).toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const entriesWithBalance = filteredEntries;









  const totalCredit = filteredEntries.filter(e => e.entry_type === 'payment_in').reduce((s, e) => s + parseFloat(e.amount), 0);
  const totalDebit = filteredEntries.filter(e => e.entry_type === 'payment_out').reduce((s, e) => s + parseFloat(e.amount), 0);

  const sendLedgerVia = async (channel) => {
    setLedgerSending(channel);
    try {
      const phone = partyType === 'customer'
        ? (party.mobile || party.mobile2 || party.mobile3 || '')
        : (party.phone || party.contact_no || '');
      const recipient = party.email || '';

      if (channel === 'whatsapp' && !phone) {
        throw new Error(`${partyType === 'customer' ? 'Customer' : 'Supplier'} phone is required to send ledger via WhatsApp`);
      }
      if (channel === 'email' && !recipient) {
        throw new Error(`${partyType === 'customer' ? 'Customer' : 'Supplier'} email is required to send ledger via Email`);
      }

      await settingsAPI.sendLedger({
        party_type: partyType,
        party_name: party.name || (partyType === 'customer' ? 'Customer' : 'Supplier'),
        channel,
        entries: entriesWithBalance,
        recipient: recipient || undefined,
        phone: phone || undefined,
      });
      alert(`✅ Ledger sent via ${channel}!`);
    } catch (err) {
      alert(`❌ ${err.response?.data?.detail || err.message || `Failed to send ledger via ${channel}`}`);
    }
    setLedgerSending('');
  };

  const handleSave = async (e, force = false) => {
    if (e) e.preventDefault();
    setSaving(true); setDupWarning(null);
    try {
      if (partyType === 'customer') {
        await customerAPI.update(party.id, { ...editForm, opening_balance: parseFloat(editForm.opening_balance) || 0 }, force);
      } else {
        await supplierAPI.update(party.id, { ...editForm, opening_balance: parseFloat(editForm.opening_balance) || 0 });
      }
      setSaving(false); onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.duplicate_warning) { setDupWarning(detail); setSaving(false); return; }
      alert(typeof detail === 'string' ? detail : 'Save failed');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (partyType === 'customer') await customerAPI.delete(party.id);
      else await supplierAPI.delete(party.id);
      onDeleted();
    } catch { alert('Delete failed'); setDeleting(false); setConfirmDelete(false); }
  };

  const statusBadge = s => {
    const map = { paid: ['#dcfce7','#166534'], unpaid: ['#fee2e2','#991b1b'], partial: ['#fef9c3','#854d0e'] };
    const [bg, tx] = map[s] || ['#f1f5f9','#374151'];
    return <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: bg, color: tx }}>{s}</span>;
  };

  const isBusiness = editForm.customer_type === 'business';
  const tabs = [
    { id: 'activity', label: partyType === 'customer' ? '📋 Invoices' : '📋 Bills' },
    { id: 'ledger', label: '📒 Ledger' },
    { id: 'edit', label: '✏️ Edit Details' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 400, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 10, overflowY: 'auto' }}
      onClick={e => e.target === e.currentTarget && handleClose()}>
      <div className="animate-scaleIn" style={{ background: darkMode ? '#0F172A' : '#E2E8F0', borderRadius: 18, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: 12, boxSizing: 'border-box', marginTop: 4 }}>
      <div style={{ background: cardBg, borderRadius: 14, border: `2px solid ${border}`, display: 'flex', flexDirection: 'column' }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h3 style={{ ...pg, fontSize: 18, fontWeight: 800, margin: 0 }}>{party.name}</h3>
              {isNew(party.created_at) && <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 800, background: '#dcfce7', color: '#15803d' }}>NEW</span>}
              {partyType === 'customer' && party.customer_type && (
                <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: party.customer_type === 'business' ? (darkMode ? '#1e3a5f' : '#dbeafe') : (darkMode ? '#1a2e1a' : '#dcfce7'), color: party.customer_type === 'business' ? (darkMode ? '#93c5fd' : '#1d4ed8') : (darkMode ? '#86efac' : '#15803d') }}>
                  {party.customer_type === 'business' ? '🏢 Business' : '👤 Normal'}
                </span>
              )}
            </div>
            <div style={{ ...muted, fontSize: 12, marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {(party.mobile || party.phone) && <span>📞 {party.mobile || party.phone}</span>}
              {party.email && <span>✉ {party.email}</span>}
              {party.gstin && <span style={{ fontFamily: 'monospace' }}>GST: {party.gstin}</span>}
              {parseFloat(party.outstanding) > 0 && <span style={{ color: '#dc2626', fontWeight: 700 }}>₹{fmtNum(party.outstanding)} due</span>}
              <span>Since {fmtDate(party.created_at)}</span>
            </div>
          </div>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', ...muted, flexShrink: 0, marginLeft: 12 }}>&times;</button>
        </div>

        {/* Tab bar */}
        <div style={{ borderBottom: `2px solid ${border}`, display: 'flex', gap: 2, padding: '0 20px', flexShrink: 0 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '10px 18px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: 'transparent',
              borderBottom: tab === t.id ? `3px solid ${acc}` : '3px solid transparent',
              color: tab === t.id ? acc : (darkMode ? '#64748B' : '#94A3B8'), marginBottom: -2,
            }}>{t.label}</button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '18px 22px' }}>

          {/* ACTIVITY TAB */}
          {tab === 'activity' && (
            actLoading ? <div style={{ textAlign: 'center', padding: 60, ...muted }}>Loading…</div> :
            activity.length === 0 ? <div style={{ textAlign: 'center', padding: 60, ...muted }}>No records yet</div> :
            <div>
              <div style={{ marginBottom: 12, fontSize: 13, ...muted }}>{activity.length} records</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                  <thead>
                    <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                      {['Date', partyType === 'customer' ? 'Invoice #' : 'Bill #', 'Mode', 'Total', 'Status'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, ...muted, textTransform: 'uppercase', borderBottom: `2px solid ${border}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activity.map((r, i) => (
                      <tr key={r.id || i} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        <td style={{ padding: '9px 12px', fontSize: 12, ...muted }}>{fmtDate(r.invoice_date || r.created_at || r.bill_date)}</td>
                        <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 700, ...pg }}>{r.invoice_number || r.bill_number || '—'}</td>
                        <td style={{ padding: '9px 12px', fontSize: 12, ...muted }}>{r.payment_mode || '—'}</td>
                        <td style={{ padding: '9px 12px', fontSize: 14, fontWeight: 800, color: acc }}>₹{fmtNum(r.grand_total || r.total_amount)}</td>
                        <td style={{ padding: '9px 12px' }}>{r.payment_status ? statusBadge(r.payment_status) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* LEDGER TAB */}
          {tab === 'ledger' && (
            ledgerLoading
              ? <div style={{ textAlign: 'center', padding: 60, ...muted }}>Loading ledger…</div>
              : <div>
                  {/* Summary cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
                    {[
                      ['Total Debit', `₹${fmtNum(totalDebit)}`, '#DC2626'],
                      ['Total Credit', `₹${fmtNum(totalCredit)}`, '#16A34A'],
                      ['Net Balance', `₹${fmtNum(Math.abs(ledger?.balance ?? 0))} ${parseFloat(ledger?.balance ?? 0) >= 0 ? 'Dr' : 'Cr'}`, parseFloat(ledger?.balance ?? 0) >= 0 ? '#DC2626' : '#16A34A'],
                      ['Entries', filteredEntries.length, acc],
                    ].map(([label, val, clr]) => (
                      <div key={label} style={{ background: darkMode ? '#0F172A' : '#F8FAFC', border: `1px solid ${border}`, borderRadius: 10, padding: '12px 14px' }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: clr }}>{val}</div>
                        <div style={{ fontSize: 11, ...muted, marginTop: 2 }}>{label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Filters */}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, padding: '10px 12px', background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 10, border: `1px solid ${border}` }}>
                    <StrictDateInput value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                      style={{ ...inp, width: 'auto', flex: '0 0 auto', fontSize: 13, padding: '7px 10px' }} />
                    <span style={{ alignSelf: 'center', ...muted, fontSize: 12 }}>to</span>
                    <StrictDateInput value={dateTo} onChange={e => setDateTo(e.target.value)}
                      style={{ ...inp, width: 'auto', flex: '0 0 auto', fontSize: 13, padding: '7px 10px' }} />
                    <select value={entryFilter} onChange={e => setEntryFilter(e.target.value)}
                      style={{ ...inp, width: 'auto', flex: '0 0 auto', fontSize: 13, padding: '7px 10px' }}>
                      <option value="all">All Entries</option>
                      <option value="payment_in">Credits Only</option>
                      <option value="payment_out">Debits Only</option>
                    </select>
                    <input value={ledgerSearch} onChange={e => setLedgerSearch(e.target.value)}
                      placeholder="Search description, mode…"
                      style={{ ...inp, flex: '1 1 140px', fontSize: 13, padding: '7px 10px' }} />
                    {(dateFrom || dateTo || entryFilter !== 'all' || ledgerSearch) && (
                      <button onClick={() => { setDateFrom(''); setDateTo(''); setEntryFilter('all'); setLedgerSearch(''); }}
                        style={{ padding: '7px 12px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontSize: 12, cursor: 'pointer', ...muted }}>
                        &times; Clear
                      </button>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                    <button
                      onClick={() => sendLedgerVia('whatsapp')}
                      disabled={entriesWithBalance.length === 0 || ledgerSending === 'whatsapp'}
                      style={{ padding: '7px 14px', background: '#25D366', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (entriesWithBalance.length === 0 || ledgerSending === 'whatsapp') ? 0.6 : 1 }}>
                      {ledgerSending === 'whatsapp' ? '⏳' : '💬'} WhatsApp
                    </button>
                    <button
                      onClick={() => sendLedgerVia('telegram')}
                      disabled={entriesWithBalance.length === 0 || ledgerSending === 'telegram'}
                      style={{ padding: '7px 14px', background: '#0088cc', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (entriesWithBalance.length === 0 || ledgerSending === 'telegram') ? 0.6 : 1 }}>
                      {ledgerSending === 'telegram' ? '⏳' : '✈️'} Telegram
                    </button>
                    <button
                      onClick={() => sendLedgerVia('email')}
                      disabled={entriesWithBalance.length === 0 || ledgerSending === 'email'}
                      style={{ padding: '7px 14px', background: '#EA4335', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (entriesWithBalance.length === 0 || ledgerSending === 'email') ? 0.6 : 1 }}>
                      {ledgerSending === 'email' ? '⏳' : '📧'} Email
                    </button>
                  </div>

                  {/* Credit/Debit Notes Section */}
                  <div style={{ marginBottom: 16, padding: 14, borderRadius: 12, border: `1px solid ${border}`, background: darkMode ? '#1E293B' : '#fff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                      <div style={{ ...pg, fontWeight: 700, fontSize: 14 }}>📝 Credit / Debit Notes</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {['all', 'credit', 'debit'].map(f => (
                            <button key={f} onClick={() => setCdFilter(f)}
                              style={{
                                padding: '6px 12px', borderRadius: 8, cursor: 'pointer', textTransform: 'capitalize', fontWeight: 600, fontSize: 12,
                                border: `1px solid ${cdFilter === f ? acc : border}`,
                                background: cdFilter === f ? acc : 'transparent',
                                color: cdFilter === f ? '#fff' : (darkMode ? '#CBD5E1' : '#64748B'),
                              }}>
                              {f === 'all' ? 'All' : `${f} Notes`}
                            </button>
                          ))}
                          <button onClick={() => { if (!showCdForm) resetCdForm(); setShowCdForm(s => !s); }}
                            style={{ padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 12, border: 'none', background: acc, color: '#fff' }}>
                            {showCdForm ? 'Cancel' : '+ Add Note'}
                          </button>
                        </div>
                    </div>

                    {showCdForm && (
                      <form onSubmit={handleCreateCdNote} style={{ marginBottom: 14, padding: 12, borderRadius: 10, border: `1px solid ${border}`, background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                          <button type="button" onClick={() => setCdType('credit')}
                            style={{ padding: '7px 14px', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', border: `1px solid ${cdType === 'credit' ? acc : border}`, background: cdType === 'credit' ? acc : 'transparent', color: cdType === 'credit' ? '#fff' : (darkMode ? '#CBD5E1' : '#64748B') }}>
                            Credit Note
                          </button>
                          <button type="button" onClick={() => setCdType('debit')}
                            style={{ padding: '7px 14px', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', border: `1px solid ${cdType === 'debit' ? acc : border}`, background: cdType === 'debit' ? acc : 'transparent', color: cdType === 'debit' ? '#fff' : (darkMode ? '#CBD5E1' : '#64748B') }}>
                            Debit Note
                          </button>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 10 }}>
                          <div>
                            <label style={lbl}>Party Name</label>
                            <input style={inp} value={cdForm.customer_name} onChange={e => setCdForm(f => ({ ...f, customer_name: e.target.value }))} required />
                          </div>
                          <div>
                            <label style={lbl}>Invoice ID (optional)</label>
                            <input style={inp} value={cdForm.invoice_id} onChange={e => setCdForm(f => ({ ...f, invoice_id: e.target.value }))} placeholder="Invoice UUID" />
                          </div>
                          <div>
                            <label style={lbl}>Reason</label>
                            <input style={inp} value={cdForm.reason} onChange={e => setCdForm(f => ({ ...f, reason: e.target.value }))} placeholder="Return / Adjustment" required />
                          </div>
                          <div>
                            <label style={lbl}>Amount (₹)</label>
                            <input type="number" min="0.01" step="0.01" style={inp} value={cdForm.amount} onChange={e => setCdForm(f => ({ ...f, amount: e.target.value }))} required />
                          </div>
                          <div>
                            <label style={lbl}>Date</label>
                            <StrictDateInput style={inp} value={cdForm.note_date} onChange={e => setCdForm(f => ({ ...f, note_date: e.target.value }))} required />
                          </div>
                          <div>
                            <label style={lbl}>Payment Mode</label>
                            <select style={inp} value={cdForm.payment_mode} onChange={e => setCdForm(f => ({ ...f, payment_mode: e.target.value }))}>
                              {['Cash','Card','UPI','Bank Transfer','Cheque','Credit','Other'].map(m => <option key={m}>{m}</option>)}
                            </select>
                          </div>
                          <div style={{ gridColumn: '1/-1' }}>
                            <label style={lbl}>Description (optional)</label>
                            <textarea style={{ ...inp, minHeight: 56, resize: 'vertical' }} value={cdForm.description} onChange={e => setCdForm(f => ({ ...f, description: e.target.value }))} placeholder="Additional details…" />
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="submit" disabled={cdSaving} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: acc, color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: cdSaving ? 0.7 : 1 }}>{cdSaving ? 'Saving...' : 'Create Note'}</button>
                        </div>
                      </form>
                    )}

                    {cdLoading ? (
                      <div style={{ ...muted, fontSize: 13 }}>Loading notes...</div>
                    ) : cdNotes.length === 0 ? (
                      <div style={{ ...muted, fontSize: 13 }}>No credit/debit notes for this party.</div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ borderBottom: `2px solid ${border}` }}>
                              {['Note No', 'Type', 'Date', 'Reason', 'Total', 'Action'].map(h => (
                                <th key={h} style={{ ...muted, fontWeight: 700, fontSize: 11, textAlign: 'left', padding: '8px 6px', textTransform: 'uppercase' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {cdNotes.map(note => (
                              <tr key={note.id} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                                <td style={{ ...pg, padding: '8px 6px', fontWeight: 700 }}>{note.note_number || '—'}</td>
                                <td style={{ padding: '8px 6px' }}>
                                  <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                                    background: note.note_type === 'credit' ? '#DCFCE7' : '#FEE2E2',
                                    color: note.note_type === 'credit' ? '#166534' : '#991B1B' }}>
                                    {(note.note_type || '—').toUpperCase()}
                                  </span>
                                </td>
                                <td style={{ ...pg, padding: '8px 6px' }}>{fmtDate(note.note_date)}</td>
                                <td style={{ ...pg, padding: '8px 6px' }}>{note.reason || '—'}{note.description ? <div style={{ fontSize: 11, ...muted }}>{note.description}</div> : null}</td>
                                <td style={{ ...pg, padding: '8px 6px', fontWeight: 700 }}>₹{fmtNum(note.grand_total)}</td>
                                <td style={{ padding: '8px 6px' }}>
                                  <button onClick={() => handleDeleteCdNote(note.id)} style={{ padding: '4px 10px', borderRadius: 8, border: `1px solid ${border}`, background: 'transparent', color: '#EF4444', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>Delete</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {filteredEntries.length === 0
                    ? <div style={{ textAlign: 'center', padding: 40, ...muted }}>No ledger entries{(dateFrom || dateTo || entryFilter !== 'all') ? ' matching filters' : ''}</div>
                    : <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                          <thead>
                            <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', position: 'sticky', top: 0 }}>
                              {['Date','Description','Mode','Reference','Debit','Credit','Balance'].map(h => (
                                <th key={h} style={{ padding: '8px 10px', textAlign: ['Debit','Credit','Balance'].includes(h) ? 'right' : 'left', fontSize: 11, fontWeight: 700, ...muted, textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: `2px solid ${border}` }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {entriesWithBalance.map((e, i) => (
                              <tr key={e.id || i} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, background: i % 2 === 1 ? (darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)') : 'transparent' }}>
                                <td style={{ padding: '8px 10px', fontSize: 12, ...muted, whiteSpace: 'nowrap' }}>{fmtDate(e.entry_date)}</td>
                                <td style={{ padding: '8px 10px', fontSize: 12, ...pg, maxWidth: 200 }}>
                                  <div>{e.description || '—'}</div>
                                  {e.reference_type && <div style={{ fontSize: 10, ...muted }}>{e.reference_type.replace(/_/g,' ')}</div>}
                                  {e.cheque_date && <div style={{ fontSize: 10, color: '#f59e0b' }}>Cheque: {fmtDate(e.cheque_date)}</div>}
                                  {e.finance_emi_date && <div style={{ fontSize: 10, color: '#8b5cf6' }}>EMI: {fmtDate(e.finance_emi_date)}</div>}
                                </td>
                                <td style={{ padding: '8px 10px', fontSize: 12, ...muted, whiteSpace: 'nowrap' }}>{e.payment_mode || '—'}</td>
                                <td style={{ padding: '8px 10px', fontSize: 11, fontFamily: 'monospace', ...muted }}>{String(e.reference_id || '').slice(0,8) || '—'}</td>
                                <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 700, textAlign: 'right', color: e.entry_type === 'payment_out' ? '#DC2626' : 'transparent' }}>
                                  {e.entry_type === 'payment_out' ? `₹${fmtNum(e.amount)}` : ''}
                                </td>
                                <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 700, textAlign: 'right', color: e.entry_type === 'payment_in' ? '#16A34A' : 'transparent' }}>
                                  {e.entry_type === 'payment_in' ? `₹${fmtNum(e.amount)}` : ''}
                                </td>
                                <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 800, textAlign: 'right', color: (e.running_balance || 0) >= 0 ? '#DC2626' : '#16A34A', whiteSpace: 'nowrap' }}>
                                  ₹{fmtNum(Math.abs(e.running_balance || 0))}
                                  <span style={{ fontSize: 9, marginLeft: 3, ...muted }}>{(e.running_balance || 0) >= 0 ? 'Dr' : 'Cr'}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderTop: `2px solid ${border}` }}>
                              <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, ...pg, fontSize: 12 }}>Total ({filteredEntries.length} entries)</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 800, color: '#DC2626', fontSize: 13 }}>₹{fmtNum(totalDebit)}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 800, color: '#16A34A', fontSize: 13 }}>₹{fmtNum(totalCredit)}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 800, fontSize: 13, color: (totalDebit - totalCredit) >= 0 ? '#DC2626' : '#16A34A' }}>₹{fmtNum(Math.abs(totalDebit - totalCredit))} {(totalDebit - totalCredit) >= 0 ? 'Dr' : 'Cr'}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                  }
                </div>
          )}

          {/* EDIT TAB */}
          {tab === 'edit' && (
            <form onSubmit={handleSave}>
              {partyType === 'customer' ? (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={lbl}>Customer Type</div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      {[['normal','👤 Normal'],['business','🏢 Business']].map(([val, label]) => (
                        <button type="button" key={val} onClick={() => { setEditDirty(true); setEditForm(f => ({ ...f, customer_type: val })); }}
                          style={{ flex: 1, padding: '9px 14px', border: `2px solid ${editForm.customer_type === val ? acc : border}`, borderRadius: 10, background: editForm.customer_type === val ? (darkMode ? '#1e3a5f' : '#eff6ff') : 'transparent', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: editForm.customer_type === val ? acc : (darkMode ? '#94A3B8' : '#64748B') }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                    <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Full Name *</label><input style={inp} required value={editForm.name} onChange={setF('name')} /></div>
                    <div><label style={lbl}>Primary Mobile *</label><input style={inp} type="tel" required value={editForm.mobile} onChange={setFMobile('mobile')} maxLength={10} inputMode="numeric" /></div>
                    <div><label style={lbl}>Email</label><input style={inp} type="email" value={editForm.email} onChange={setF('email')} /></div>
                    <div><label style={lbl}>Mobile 2</label><input style={inp} type="tel" value={editForm.mobile2} onChange={setFMobile('mobile2')} maxLength={10} inputMode="numeric" /></div>
                    <div><label style={lbl}>Mobile 3</label><input style={inp} type="tel" value={editForm.mobile3} onChange={setFMobile('mobile3')} maxLength={10} inputMode="numeric" /></div>
                    <div><label style={lbl}>Landline / Phone</label><input style={inp} value={editForm.phone} onChange={setF('phone')} /></div>
                    <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Address</label><textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={editForm.address} onChange={setF('address')} /></div>
                    <div><label style={lbl}>City</label><input style={inp} value={editForm.city} onChange={setF('city')} /></div>
                    <div><label style={lbl}>Area</label><input style={inp} value={editForm.area} onChange={setF('area')} /></div>
                    <div><label style={lbl}>Pincode</label><input style={inp} value={editForm.pincode} onChange={setF('pincode')} maxLength={6} /></div>
                    <div><label style={lbl}>State</label><select style={inp} value={editForm.state} onChange={setF('state')}>{INDIA_STATES.map(s => <option key={s}>{s}</option>)}</select></div>
                    <div><label style={lbl}>Country</label><input style={inp} value={editForm.country} onChange={setF('country')} /></div>
                    {isBusiness && <>
                      <div><label style={lbl}>GSTIN</label><input style={inp} value={editForm.gstin} onChange={setF('gstin')} maxLength={15} /></div>
                      <div><label style={lbl}>PAN</label><input style={inp} value={editForm.pan} onChange={setF('pan')} maxLength={10} /></div>
                      <div><label style={lbl}>GST Type</label><select style={inp} value={editForm.gst_type} onChange={setF('gst_type')}><option value="">--</option><option value="Regular">Regular</option><option value="Composition">Composition</option><option value="Unregistered">Unregistered</option><option value="Consumer">Consumer</option><option value="SEZ">SEZ</option></select></div>
                      <div><label style={lbl}>Legal Name</label><input style={inp} value={editForm.legal_name} onChange={setF('legal_name')} /></div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}><input type="checkbox" checked={editForm.gst_verified} onChange={e => { setEditDirty(true); setEditForm(f => ({ ...f, gst_verified: e.target.checked })); }} /><label style={{ ...lbl, margin: 0 }}>GST Verified</label></div>
                      <div><label style={lbl}>Contact Person</label><input style={inp} value={editForm.contact_person} onChange={setF('contact_person')} /></div>
                      <div><label style={lbl}>Contact Number</label><input style={inp} value={editForm.contact_no} onChange={setF('contact_no')} /></div>
                      <div><label style={lbl}>Document Type</label><select style={inp} value={editForm.document_type} onChange={setF('document_type')}><option value="">--</option><option value="Aadhaar">Aadhaar</option><option value="PAN">PAN</option><option value="Passport">Passport</option><option value="DrivingLicense">Driving License</option><option value="VoterID">Voter ID</option></select></div>
                      <div><label style={lbl}>Document No.</label><input style={inp} value={editForm.document_no} onChange={setF('document_no')} /></div>
                      <div><label style={lbl}>Doc Issue Date</label><StrictDateInput style={inp}  value={editForm.doc_issue_date} onChange={setF('doc_issue_date')} /></div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}><input type="checkbox" checked={editForm.credit_allowed} onChange={e => { setEditDirty(true); setEditForm(f => ({ ...f, credit_allowed: e.target.checked })); }} /><label style={{ ...lbl, margin: 0 }}>Credit Allowed</label></div>
                      <div><label style={lbl}>Credit Limit (₹)</label><input style={inp} type="number" step="0.01" value={editForm.credit_limit} onChange={setF('credit_limit')} /></div>
                      <div><label style={lbl}>Price Category</label><select style={inp} value={editForm.price_category} onChange={setF('price_category')}><option value="retail">Retail</option><option value="wholesale">Wholesale</option><option value="dealer">Dealer</option><option value="distributor">Distributor</option></select></div>
                    </>}
                    {!isBusiness && <><div><label style={lbl}>Birthday</label><StrictDateInput style={inp}  value={editForm.birthday} onChange={setF('birthday')} /></div><div><label style={lbl}>Anniversary</label><StrictDateInput style={inp}  value={editForm.anniversary} onChange={setF('anniversary')} /></div></>}
                    <div><label style={lbl}>Opening Balance (₹)</label><input style={inp} type="number" step="0.01" value={editForm.opening_balance} onChange={setF('opening_balance')} /></div>
                    <div><label style={lbl}>Balance Type</label><select style={inp} value={editForm.op_bal_type} onChange={setF('op_bal_type')}><option value="debit">Debit</option><option value="credit">Credit</option></select></div>
                    <div><label style={lbl}>Tags</label><input style={inp} value={editForm.tags} onChange={setF('tags')} placeholder="vip, wholesale" /></div>
                    <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Notes</label><textarea style={{ ...inp, minHeight: 54, resize: 'vertical' }} value={editForm.notes} onChange={setF('notes')} /></div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                  <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Supplier Name *</label><input style={inp} required value={editForm.name} onChange={setF('name')} /></div>
                  <div><label style={lbl}>Contact Person</label><input style={inp} value={editForm.contact_person} onChange={setF('contact_person')} /></div>
                  <div><label style={lbl}>Phone *</label><input style={inp} type="tel" required value={editForm.phone} onChange={setFMobile('phone')} maxLength={10} inputMode="numeric" /></div>
                  <div><label style={lbl}>Contact No.</label><input style={inp} value={editForm.contact_no} onChange={setF('contact_no')} /></div>
                  <div><label style={lbl}>Email</label><input style={inp} type="email" value={editForm.email} onChange={setF('email')} /></div>
                  <div><label style={lbl}>GSTIN</label><input style={inp} value={editForm.gstin} onChange={setF('gstin')} maxLength={15} /></div>
                  <div><label style={lbl}>PAN</label><input style={inp} value={editForm.pan} onChange={setF('pan')} maxLength={10} /></div>
                  <div><label style={lbl}>Tax State</label><input style={inp} value={editForm.tax_state} onChange={setF('tax_state')} placeholder="e.g. Maharashtra" /></div>
                  <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Address</label><textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={editForm.address} onChange={setF('address')} /></div>
                  <div><label style={lbl}>City</label><input style={inp} value={editForm.city} onChange={setF('city')} /></div>
                  <div><label style={lbl}>Area</label><input style={inp} value={editForm.area} onChange={setF('area')} /></div>
                  <div><label style={lbl}>Pincode</label><input style={inp} value={editForm.pincode} onChange={setF('pincode')} maxLength={6} /></div>
                  <div><label style={lbl}>State</label><input style={inp} value={editForm.state} onChange={setF('state')} /></div>
                  <div><label style={lbl}>Country</label><input style={inp} value={editForm.country} onChange={setF('country')} /></div>
                  <div><label style={lbl}>Opening Balance (₹)</label><input style={inp} type="number" step="0.01" value={editForm.opening_balance} onChange={setF('opening_balance')} /></div>
                  <div><label style={lbl}>Balance Type</label><select style={inp} value={editForm.op_bal_type} onChange={setF('op_bal_type')}><option value="debit">Debit</option><option value="credit">Credit</option></select></div>
                  <div><label style={lbl}>Status</label><select style={inp} value={editForm.status} onChange={setF('status')}><option value="active">🟢 Active</option><option value="inactive">🔴 Inactive</option></select></div>
                  <div><label style={lbl}>Bank Name</label><input style={inp} value={editForm.bank_name} onChange={setF('bank_name')} /></div>
                  <div><label style={lbl}>Bank IFSC</label><input style={inp} value={editForm.bank_ifsc} onChange={setF('bank_ifsc')} /></div>
                  <div><label style={lbl}>Bank Account No.</label><input style={inp} value={editForm.bank_account} onChange={setF('bank_account')} /></div>
                  <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Notes</label><textarea style={{ ...inp, minHeight: 54, resize: 'vertical' }} value={editForm.notes} onChange={setF('notes')} /></div>
                </div>
              )}

              {dupWarning && (
                <div style={{ marginTop: 14, padding: 14, background: '#fef3c7', border: '2px solid #f59e0b', borderRadius: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 6 }}>⚠️ Duplicate Found</div>
                  {(dupWarning.duplicates || []).map((d, i) => <div key={i} style={{ fontSize: 13, color: '#78350f' }}><strong>{d.name}</strong> — {d.matched.join(', ')}</div>)}
                  <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                    <button type="button" onClick={() => handleSave(null, true)} style={{ padding: '7px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Save Anyway</button>
                    <button type="button" onClick={() => setDupWarning(null)} style={{ padding: '7px 14px', background: 'transparent', border: '1px solid #f59e0b', borderRadius: 7, fontSize: 12, cursor: 'pointer', color: '#92400e' }}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Danger zone */}
              <div style={{ marginTop: 24, padding: '14px 16px', border: '1.5px dashed #ef4444', borderRadius: 10, background: darkMode ? 'rgba(239,68,68,0.05)' : '#fff5f5' }}>
                <div style={{ fontWeight: 700, color: '#dc2626', fontSize: 13, marginBottom: 6 }}>⚠️ Danger Zone</div>
                <p style={{ fontSize: 12, color: '#ef4444', margin: '0 0 10px' }}>
                  Permanently delete this {partyType}. This cannot be undone.
                </p>
                {!confirmDelete ? (
                  <button type="button" onClick={() => setConfirmDelete(true)} style={{ padding: '7px 16px', background: 'transparent', border: '1.5px solid #ef4444', borderRadius: 8, color: '#dc2626', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                    🗑 Delete {partyType === 'customer' ? 'Customer' : 'Supplier'}
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>Are you sure?</span>
                    <button type="button" onClick={handleDelete} disabled={deleting} style={{ padding: '7px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>{deleting ? 'Deleting…' : 'Yes, Delete'}</button>
                    <button type="button" onClick={() => setConfirmDelete(false)} style={{ padding: '7px 12px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 7, fontSize: 12, cursor: 'pointer', ...muted }}>Cancel</button>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={handleClose} style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', ...muted }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding: '9px 28px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </form>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

/* ================================================================
   ADD CUSTOMER MODAL
================================================================= */
const AddCustomerModal = ({ onClose, onSaved }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const [form, setForm] = useState({ name: '', customer_type: 'normal', mobile: '', mobile2: '', mobile3: '', email: '', phone: '', birthday: '', anniversary: '', address: '', city: '', area: '', pincode: '', state: 'Maharashtra', country: 'India', gstin: '', pan: '', tags: '', notes: '', opening_balance: '', op_bal_type: 'debit', contact_person: '', contact_no: '', document_type: '', document_no: '', doc_issue_date: '', credit_allowed: true, credit_limit: '', price_category: 'retail', gst_type: '', gst_verified: false, legal_name: '' });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);
  const set = k => e => { setDirty(true); setForm(f => ({ ...f, [k]: e.target.value })); };
  const setMobile = k => e => { setDirty(true); const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 10); setForm(f => ({ ...f, [k]: v })); };
  const isBusiness = form.customer_type === 'business';

  const handleClose = useCallback(() => {
    if (dirty && !window.confirm('Discard this customer? All unsaved changes will be lost.')) return;
    onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [handleClose]);

  const handleSubmit = async (e, force = false) => {
    if (e) e.preventDefault();
    if (!form.name.trim()) { alert('Name is required'); return; }
    if (!form.mobile.trim()) { alert('Mobile is required'); return; }
    if (form.mobile.length !== 10) { alert('Primary mobile must be exactly 10 digits'); return; }
    setSaving(true); setDupWarning(null);
    try {
      await customerAPI.create({ ...form, opening_balance: parseFloat(form.opening_balance) || 0, credit_limit: form.credit_limit !== '' ? parseFloat(form.credit_limit) || 0 : null }, force);
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.duplicate_warning) { setDupWarning(detail); setSaving(false); return; }
      alert(typeof detail === 'string' ? detail : 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 10, overflowY: 'auto' }}>
      <div className="animate-scaleIn" style={{ background: darkMode ? '#0F172A' : '#E2E8F0', borderRadius: 18, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: 12, boxSizing: 'border-box', marginTop: 4 }}>
      <div style={{ background: cardBg, borderRadius: 14, border: `2px solid ${border}` }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', position: 'sticky', top: 0, background: cardBg, zIndex: 10 }}>
            <h3 style={{ ...pg, fontSize: 17, fontWeight: 800, margin: 0 }}>➕ Add Customer</h3>
            <button type="button" onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', ...muted }}>×</button>
          </div>
          <div style={{ padding: '16px' }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              {[['normal','👤 Normal'],['business','🏢 Business']].map(([val, label]) => (
                <button type="button" key={val} onClick={() => { setDirty(true); setForm(f => ({ ...f, customer_type: val })); }}
                  style={{ flex: 1, padding: '9px 14px', border: `2px solid ${form.customer_type === val ? acc : border}`, borderRadius: 10, background: form.customer_type === val ? (darkMode ? '#1e3a5f' : '#eff6ff') : 'transparent', cursor: 'pointer', fontWeight: 700, fontSize: 13, color: form.customer_type === val ? acc : (darkMode ? '#94A3B8' : '#64748B') }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Full Name *</label><input style={inp} required value={form.name} onChange={set('name')} /></div>
              <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Address</label><textarea style={{ ...inp, minHeight: 54, resize: 'vertical' }} value={form.address} onChange={set('address')} /></div>
              <div><label style={lbl}>Primary Mobile *</label><input style={inp} type="tel" required value={form.mobile} onChange={setMobile('mobile')} maxLength={10} inputMode="numeric" /></div>
              <div><label style={lbl}>Mobile 2</label><input style={inp} type="tel" value={form.mobile2} onChange={setMobile('mobile2')} maxLength={10} inputMode="numeric" /></div>
              <div><label style={lbl}>Mobile 3</label><input style={inp} type="tel" value={form.mobile3} onChange={setMobile('mobile3')} maxLength={10} inputMode="numeric" /></div>
              <div><label style={lbl}>Email</label><input style={inp} type="email" value={form.email} onChange={set('email')} /></div>
              <div><label style={lbl}>Landline / Phone</label><input style={inp} value={form.phone} onChange={set('phone')} /></div>
              <div><label style={lbl}>City</label><input style={inp} value={form.city} onChange={set('city')} /></div>
              <div><label style={lbl}>Area</label><input style={inp} value={form.area} onChange={set('area')} /></div>
              <div><label style={lbl}>Pincode</label><input style={inp} value={form.pincode} onChange={set('pincode')} maxLength={6} /></div>
              <div><label style={lbl}>State</label><select style={inp} value={form.state} onChange={set('state')}>{INDIA_STATES.map(s => <option key={s}>{s}</option>)}</select></div>
              <div><label style={lbl}>Country</label><input style={inp} value={form.country} onChange={set('country')} /></div>
              {isBusiness && <>
                <div><label style={lbl}>GSTIN</label><input style={inp} value={form.gstin} onChange={set('gstin')} maxLength={15} /></div>
                <div><label style={lbl}>PAN</label><input style={inp} value={form.pan} onChange={set('pan')} maxLength={10} /></div>
                <div><label style={lbl}>GST Type</label><select style={inp} value={form.gst_type} onChange={set('gst_type')}><option value="">--</option><option value="Regular">Regular</option><option value="Composition">Composition</option><option value="Unregistered">Unregistered</option></select></div>
                <div><label style={lbl}>Legal Name</label><input style={inp} value={form.legal_name} onChange={set('legal_name')} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}><input type="checkbox" checked={form.gst_verified} onChange={e => { setDirty(true); setForm(f => ({ ...f, gst_verified: e.target.checked })); }} /><label style={{ ...lbl, margin: 0 }}>GST Verified</label></div>
                <div><label style={lbl}>Contact Person</label><input style={inp} value={form.contact_person} onChange={set('contact_person')} /></div>
                <div><label style={lbl}>Contact Number</label><input style={inp} value={form.contact_no} onChange={set('contact_no')} /></div>
                <div><label style={lbl}>Document Type</label><select style={inp} value={form.document_type} onChange={set('document_type')}><option value="">--</option><option value="Aadhaar">Aadhaar</option><option value="PAN">PAN</option><option value="Passport">Passport</option><option value="DrivingLicense">DL</option></select></div>
                <div><label style={lbl}>Document No.</label><input style={inp} value={form.document_no} onChange={set('document_no')} /></div>
                <div><label style={lbl}>Doc Issue Date</label><StrictDateInput style={inp}  value={form.doc_issue_date} onChange={set('doc_issue_date')} /></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 18 }}><input type="checkbox" checked={form.credit_allowed} onChange={e => { setDirty(true); setForm(f => ({ ...f, credit_allowed: e.target.checked })); }} /><label style={{ ...lbl, margin: 0 }}>Credit Allowed</label></div>
                <div><label style={lbl}>Credit Limit (₹)</label><input style={inp} type="number" step="0.01" value={form.credit_limit} onChange={set('credit_limit')} /></div>
                <div><label style={lbl}>Price Category</label><select style={inp} value={form.price_category} onChange={set('price_category')}><option value="retail">Retail</option><option value="wholesale">Wholesale</option><option value="dealer">Dealer</option></select></div>
              </>}
              {!isBusiness && <><div><label style={lbl}>Birthday</label><StrictDateInput style={inp}  value={form.birthday} onChange={set('birthday')} /></div><div><label style={lbl}>Anniversary</label><StrictDateInput style={inp}  value={form.anniversary} onChange={set('anniversary')} /></div></>}
              <div><label style={lbl}>Opening Balance (₹)</label><input style={inp} type="number" step="0.01" value={form.opening_balance} onChange={set('opening_balance')} /></div>
              <div><label style={lbl}>Balance Type</label><select style={inp} value={form.op_bal_type} onChange={set('op_bal_type')}><option value="debit">Debit</option><option value="credit">Credit</option></select></div>
              <div><label style={lbl}>Tags</label><input style={inp} value={form.tags} onChange={set('tags')} placeholder="vip, wholesale" /></div>
              <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Notes</label><textarea style={{ ...inp, minHeight: 50, resize: 'vertical' }} value={form.notes} onChange={set('notes')} /></div>
            </div>
            {dupWarning && (
              <div style={{ marginTop: 14, padding: 14, background: '#fef3c7', border: '2px solid #f59e0b', borderRadius: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 6 }}>⚠️ Duplicate Found</div>
                {(dupWarning.duplicates || []).map((d, i) => <div key={i} style={{ fontSize: 13, color: '#78350f' }}><strong>{d.name}</strong> — {d.matched.join(', ')}</div>)}
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => handleSubmit(null, true)} style={{ padding: '7px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>Save Anyway</button>
                  <button type="button" onClick={() => setDupWarning(null)} style={{ padding: '7px 14px', background: 'transparent', border: '1px solid #f59e0b', borderRadius: 7, fontSize: 12, cursor: 'pointer', color: '#92400e' }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
          <div style={{ padding: '12px 16px', borderTop: `1px solid ${border}`, display: 'flex', gap: 8, justifyContent: 'flex-end', position: 'sticky', bottom: 0, background: cardBg }}>
            <button type="button" onClick={handleClose} style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', ...muted }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '9px 28px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Add Customer'}</button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
};

/* ================================================================
   ADD SUPPLIER MODAL
================================================================= */
const AddSupplierModal = ({ onClose, onSaved }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const [form, setForm] = useState({ name: '', contact_person: '', phone: '', contact_no: '', email: '', address: '', city: '', area: '', pincode: '', state: 'Maharashtra', country: 'India', gstin: '', pan: '', tax_state: 'Maharashtra', opening_balance: 0, op_bal_type: 'debit', bank_name: '', bank_ifsc: '', bank_account: '', status: 'active', notes: '' });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const set = k => e => { setDirty(true); setForm(f => ({ ...f, [k]: e.target.value })); };
  const setMobile = k => e => { setDirty(true); const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 10); setForm(f => ({ ...f, [k]: v })); };

  const handleClose = useCallback(() => {
    if (dirty && !window.confirm('Discard this supplier? All unsaved changes will be lost.')) return;
    onClose();
  }, [dirty, onClose]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [handleClose]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) { alert('Supplier name is required'); return; }
    if (!form.phone.trim()) { alert('Phone is required'); return; }
    if (form.phone.length !== 10) { alert('Phone must be exactly 10 digits'); return; }
    setSaving(true);
    try {
      await supplierAPI.create({ ...form, opening_balance: parseFloat(form.opening_balance) || 0 });
      onSaved();
    } catch (err) { alert(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 10, overflowY: 'auto' }}>
      <div className="animate-scaleIn" style={{ background: darkMode ? '#0F172A' : '#E2E8F0', borderRadius: 18, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: 12, boxSizing: 'border-box', marginTop: 4 }}>
      <div style={{ background: cardBg, borderRadius: 14, border: `2px solid ${border}` }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: cardBg, zIndex: 10 }}>
            <h3 style={{ ...pg, fontSize: 17, fontWeight: 800, margin: 0 }}>➕ Add Supplier</h3>
            <button type="button" onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: darkMode ? '#64748B' : '#94A3B8' }}>×</button>
          </div>
          <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Supplier Name *</label><input style={inp} required value={form.name} onChange={set('name')} /></div>
            <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Address</label><textarea style={{ ...inp, minHeight: 60, resize: 'vertical' }} value={form.address} onChange={set('address')} /></div>
            <div><label style={lbl}>Contact Person</label><input style={inp} value={form.contact_person} onChange={set('contact_person')} /></div>
            <div><label style={lbl}>Primary Mobile *</label><input style={inp} type="tel" required value={form.phone} onChange={setMobile('phone')} maxLength={10} inputMode="numeric" /></div>
            <div><label style={lbl}>Mobile 2</label><input style={inp} type="tel" value={form.contact_no} onChange={setMobile('contact_no')} maxLength={10} inputMode="numeric" /></div>
            <div><label style={lbl}>Email</label><input style={inp} type="email" value={form.email} onChange={set('email')} /></div>
            <div><label style={lbl}>GSTIN</label><input style={inp} value={form.gstin} onChange={set('gstin')} maxLength={15} /></div>
            <div><label style={lbl}>PAN</label><input style={inp} value={form.pan} onChange={set('pan')} maxLength={10} /></div>
            <div><label style={lbl}>Tax State</label><input style={inp} value={form.tax_state} onChange={set('tax_state')} placeholder="e.g. Maharashtra" /></div>
            <div><label style={lbl}>City</label><input style={inp} value={form.city} onChange={set('city')} /></div>
            <div><label style={lbl}>Area</label><input style={inp} value={form.area} onChange={set('area')} /></div>
            <div><label style={lbl}>Pincode</label><input style={inp} value={form.pincode} onChange={set('pincode')} maxLength={6} /></div>
            <div><label style={lbl}>State</label><input style={inp} value={form.state} onChange={set('state')} /></div>
            <div><label style={lbl}>Country</label><input style={inp} value={form.country} onChange={set('country')} /></div>
            <div><label style={lbl}>Opening Balance (₹)</label><input style={inp} type="number" step="0.01" value={form.opening_balance} onChange={set('opening_balance')} /></div>
            <div><label style={lbl}>Balance Type</label><select style={inp} value={form.op_bal_type} onChange={set('op_bal_type')}><option value="debit">Debit</option><option value="credit">Credit</option></select></div>
            <div><label style={lbl}>Status</label><select style={inp} value={form.status} onChange={set('status')}><option value="active">🟢 Active</option><option value="inactive">🔴 Inactive</option></select></div>
            <div><label style={lbl}>Bank Name</label><input style={inp} value={form.bank_name} onChange={set('bank_name')} /></div>
            <div><label style={lbl}>Bank IFSC</label><input style={inp} value={form.bank_ifsc} onChange={set('bank_ifsc')} /></div>
            <div><label style={lbl}>Bank Account No.</label><input style={inp} value={form.bank_account} onChange={set('bank_account')} /></div>
            <div style={{ gridColumn: '1/-1' }}><label style={lbl}>Notes</label><textarea style={{ ...inp, minHeight: 50, resize: 'vertical' }} value={form.notes} onChange={set('notes')} /></div>
          </div>
          <div style={{ padding: '12px 16px', borderTop: `1px solid ${border}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={handleClose} style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', ...muted }}>Cancel</button>
           <button type="submit" disabled={saving} style={{ padding: '9px 24px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving…' : 'Add Supplier'}</button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
};

/* ================================================================
   OUTSTANDING PANEL
================================================================= */
const OutstandingPanel = ({ partyType }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const border = darkMode ? '#334155' : '#E2E8F0';
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '8px 10px', fontSize: 13, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', mode: 'Cash', date: new Date().toISOString().split('T')[0] });
  const [paySaving, setPaySaving] = useState(false);

  const loadData = useCallback(() => {
    const api = partyType === 'customer' ? customerAPI.outstanding() : supplierAPI.outstanding();
    api.then(r => { setData(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, [partyType]);

  useEffect(() => { loadData(); }, [loadData]);

  const openPay = (party) => {
    setPayingId(party.id);
    setPayForm({ amount: String(parseFloat(party.outstanding || 0).toFixed(2)), mode: 'Cash', date: new Date().toISOString().split('T')[0] });
  };
  const closePay = () => { setPayingId(null); };

  const handleRecordPayment = async (party) => {
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) { alert('Enter a valid amount'); return; }
    setPaySaving(true);
    try {
      await paymentAPI.recordDirect({
        party_type: partyType,
        party_id: party.id,
        party_name: party.name,
        amount: amt,
        payment_mode: payForm.mode,
        payment_date: payForm.date,
        direction: partyType === 'customer' ? 'in' : 'out',
        description: `Payment from outstanding`,
      });
      alert('Payment recorded');
      setPayingId(null);
      loadData();
    } catch (err) { alert(err.response?.data?.detail || 'Payment failed'); }
    setPaySaving(false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, ...muted }}>Loading…</div>;
  if (data.length === 0) return (
    <div style={{ ...card, padding: 60, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 10 }}>🎉</div>
      <p style={muted}>No outstanding balances!</p>
    </div>
  );
  const total = data.reduce((s, c) => s + parseFloat(c.outstanding || 0), 0);
  return (
    <div>
      <div style={{ ...card, padding: '12px 18px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 15, ...pg }}>{data.length} with outstanding</div>
        <div style={{ fontWeight: 800, fontSize: 17, color: '#dc2626' }}>Total: ₹{fmtNum(total)}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.map(c => (
          <div key={c.id} style={{ ...card, padding: '14px 18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 14, ...pg }}>{c.name}</div>
                {(c.mobile || c.phone) && <div style={{ fontSize: 12, ...muted }}>📞 {c.mobile || c.phone}</div>}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#dc2626' }}>₹{fmtNum(c.outstanding)}</div>
                  <div style={{ fontSize: 11, ...muted }}>Outstanding</div>
                </div>
                {partyType === 'customer' && c.invoice_count != null && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: acc }}>{c.invoice_count}</div>
                    <div style={{ fontSize: 11, ...muted }}>Invoices</div>
                  </div>
                )}
                {(c.mobile || c.phone) && (
                  <a href={`tel:${c.mobile || c.phone}`} style={{ padding: '6px 12px', background: '#16a34a', color: '#fff', borderRadius: 7, textDecoration: 'none', fontWeight: 700, fontSize: 12 }}>📞</a>
                )}
                <button onClick={() => payingId === c.id ? closePay() : openPay(c)}
                  style={{ padding: '6px 12px', background: payingId === c.id ? '#94a3b8' : acc, color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                  {payingId === c.id ? '✕ Cancel' : '💳 Pay'}
                </button>
              </div>
            </div>
            {payingId === c.id && (
              <div style={{ marginTop: 12, padding: 12, background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 10, border: `1px solid ${border}` }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, ...muted, marginBottom: 4, textTransform: 'uppercase', fontWeight: 700 }}>Amount (₹)</div>
                    <input type="number" min="0.01" step="0.01" style={inp} value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, ...muted, marginBottom: 4, textTransform: 'uppercase', fontWeight: 700 }}>Mode</div>
                    <select style={inp} value={payForm.mode} onChange={e => setPayForm(f => ({ ...f, mode: e.target.value }))}>
                      {['Cash','Card','UPI','Bank Transfer','Cheque','Credit','Other'].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, ...muted, marginBottom: 4, textTransform: 'uppercase', fontWeight: 700 }}>Date</div>
                    <StrictDateInput style={inp} value={payForm.date} onChange={e => setPayForm(f => ({ ...f, date: e.target.value }))} />
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <button disabled={paySaving} onClick={() => handleRecordPayment(c)}
                    style={{ padding: '7px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: paySaving ? 0.7 : 1 }}>
                    {paySaving ? 'Recording…' : '✓ Record Payment'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ================================================================
   MAIN PARTIES PAGE
================================================================= */
const Parties = () => {
  const { darkMode, color } = useTheme();
  const { refreshCustomers, refreshSuppliers } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const sourceRoute = useRef(searchParams.get('source') || null);
  const pendingAction = useRef(searchParams.get('action'));
  const pendingType = useRef(searchParams.get('type'));
  const [mode, setMode] = useState(searchParams.get('mode') === 'supplier' || searchParams.get('type') === 'supplier' ? 'supplier' : 'customer');
  const [customerTab, setCustomerTab] = useState('list');
  const [supplierTab, setSupplierTab] = useState('list');
  const acc = color.swatch;
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none' };

  const [customers, setCustomers] = useState([]);
  const [custLoading, setCustLoading] = useState(true);
  const [custSearch, setCustSearch] = useState('');
  const [custType, setCustType] = useState('');
  const [custPage, setCustPage] = useState(1);
  const [custTotal, setCustTotal] = useState(0);
  const [custPages, setCustPages] = useState(1);
  const [custStats, setCustStats] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [supLoading, setSupLoading] = useState(true);
  const [supSearch, setSupSearch] = useState('');
  const [supTotal, setSupTotal] = useState(0);

  const [showAddCust, setShowAddCust] = useState(pendingAction.current === 'create' && pendingType.current !== 'supplier');
  const [showAddSup, setShowAddSup] = useState(pendingAction.current === 'create' && pendingType.current === 'supplier');
  const [detailsParty, setDetailsParty] = useState(null);

  // Clean up query params
  useEffect(() => {
    if (searchParams.get('action') || searchParams.get('source') || searchParams.get('type')) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('action'); next.delete('source'); next.delete('type');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Listen for sv-open-modal events (F3 Add Customer, F5 Add Supplier)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail === 'add-customer') { setMode('customer'); setShowAddCust(true); }
      else if (e.detail === 'add-supplier') { setMode('supplier'); setShowAddSup(true); }
    };
    window.addEventListener('sv-open-modal', handler);
    return () => window.removeEventListener('sv-open-modal', handler);
  }, []);

  const [selectedIdx, setSelectedIdx] = useState(-1);
  const pageRef = useRef(null);

  const loadCustomers = useCallback(async () => {
    setCustLoading(true);
    try {
      const res = await customerAPI.list({ page: custPage, limit: 15, search: custSearch || undefined });
      const all = res.data.customers || [];
      setCustomers(custType ? all.filter(c => c.customer_type === custType) : all);
      setCustPages(res.data.pages || 1);
      setCustTotal(res.data.total || 0);
    } catch {}
    setCustLoading(false);
  }, [custPage, custSearch, custType]);

  const loadSuppliers = useCallback(async () => {
    setSupLoading(true);
    try {
      const r = await supplierAPI.list({ search: supSearch || undefined, page_size: 50 });
      setSuppliers(r.data.suppliers || []);
      setSupTotal(r.data.suppliers?.length || 0);
    } catch {}
    setSupLoading(false);
  }, [supSearch]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);
  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);
  useEffect(() => { customerAPI.stats().then(r => setCustStats(r.data)).catch(() => {}); }, []);
  useEffect(() => { setSelectedIdx(-1); }, [customers, suppliers, mode]);

  const currentList = mode === 'customer' ? customers : suppliers;

  const handleKeyDown = e => {
    if (detailsParty || showAddCust || showAddSup) return;
    const len = currentList.length;
    if (!len) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, len - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && selectedIdx >= 0) {
      e.preventDefault();
      setDetailsParty({ party: currentList[selectedIdx], partyType: mode });
    } else if (e.key === 'Escape') {
      if (selectedIdx >= 0) { setSelectedIdx(-1); }
      else if (sourceRoute.current) { const dest = sourceRoute.current; sourceRoute.current = null; navigate(dest, { replace: true }); }
    }
    else if ((e.key === 'n' || e.key === 'N') && !e.target.closest?.('input,textarea,select')) {
      e.preventDefault();
      mode === 'customer' ? setShowAddCust(true) : setShowAddSup(true);
    }
  };

  useEffect(() => {
    if (selectedIdx >= 0 && pageRef.current) {
      const row = pageRef.current.querySelector(`tr[data-idx="${selectedIdx}"]`);
      if (row) row.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIdx]);

  const typeBadge = t => t === 'business'
    ? <span style={{ padding: '2px 7px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: darkMode ? '#1e3a5f' : '#dbeafe', color: darkMode ? '#93c5fd' : '#1d4ed8' }}>🏢 Biz</span>
    : <span style={{ padding: '2px 7px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: darkMode ? '#1a2e1a' : '#dcfce7', color: darkMode ? '#86efac' : '#15803d' }}>👤 Normal</span>;

  const totalCust = custStats?.total_customers ?? custTotal;
  const newThisMonth = custStats?.new_this_month ?? 0;
  const bdayThisMonth = custStats?.birthdays_this_month ?? 0;
  const anniThisMonth = custStats?.anniversaries_this_month ?? 0;

  const BLUE = '#3B82F6';
  const PURPLE = '#8B5CF6';

  const refreshStats = () => customerAPI.stats().then(r => setCustStats(r.data)).catch(() => {});

  return (
    <Layout>
      <div ref={pageRef} className="page-enter" style={{ maxWidth: 1200, margin: '0 auto', outline: 'none' }} onKeyDown={handleKeyDown} tabIndex={0}>

        {/* Header */}
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ ...pg, fontSize: 26, fontWeight: 800, margin: 0 }}>Parties</h1>
          <p style={{ ...muted, margin: '3px 0 0', fontSize: 13 }}>
            Customers &amp; Suppliers &nbsp;&middot;&nbsp;
            <kbd style={{ padding: '1px 5px', borderRadius: 4, background: darkMode ? '#334155' : '#E2E8F0', fontSize: 11 }}>&uarr;&darr;</kbd> navigate &nbsp;
            <kbd style={{ padding: '1px 5px', borderRadius: 4, background: darkMode ? '#334155' : '#E2E8F0', fontSize: 11 }}>Enter</kbd> open &nbsp;
            <kbd style={{ padding: '1px 5px', borderRadius: 4, background: darkMode ? '#334155' : '#E2E8F0', fontSize: 11 }}>N</kbd> add new
          </p>
        </div>

        {/* Stats dashboard */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Customers', val: totalCust, clr: BLUE },
            { label: 'Suppliers', val: supTotal, clr: PURPLE },
            { label: 'New This Month', val: newThisMonth, clr: '#16A34A', badge: newThisMonth > 0 },
            { label: '🎂 Birthdays', val: bdayThisMonth, clr: '#EC4899', sub: 'This month' },
            { label: '💍 Anniv.', val: anniThisMonth, clr: '#F59E0B', sub: 'This month' },
          ].map(({ label, val, clr, badge, sub }) => (
            <div key={label} style={{ ...card, padding: '14px 16px', position: 'relative' }}>
              {badge && <span style={{ position: 'absolute', top: 8, right: 8, padding: '1px 6px', borderRadius: 10, fontSize: 9, fontWeight: 800, background: '#dcfce7', color: '#15803d' }}>NEW</span>}
              <div style={{ fontSize: 22, fontWeight: 800, color: clr }}>{val ?? '—'}</div>
              <div style={{ fontSize: 12, ...muted, marginTop: 2 }}>{label}</div>
              {sub && <div style={{ fontSize: 10, color: clr, marginTop: 2, fontWeight: 600 }}>{sub}</div>}
            </div>
          ))}
        </div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 14, marginBottom: 22, flexWrap: 'wrap' }}>
          {[
            { key: 'customer', icon: '👥', label: 'Customer', clr: BLUE, bg: darkMode ? 'linear-gradient(135deg,#1e3a5f,#1E293B)' : 'linear-gradient(135deg,#eff6ff,#dbeafe)' },
            { key: 'supplier', icon: '🏭', label: 'Supplier', clr: PURPLE, bg: darkMode ? 'linear-gradient(135deg,#2e1a4a,#1E293B)' : 'linear-gradient(135deg,#f5f3ff,#ede9fe)' },
          ].map(({ key, icon, label, clr, bg }) => (
            <button key={key} onClick={() => { setMode(key); setSelectedIdx(-1); }}
              style={{ flex: 1, minWidth: 200, maxWidth: 340, padding: '18px 22px', borderRadius: 14, cursor: 'pointer', background: mode === key ? bg : (darkMode ? '#1E293B' : '#fff'), border: `2.5px solid ${mode === key ? clr : (darkMode ? '#334155' : '#E2E8F0')}`, boxShadow: mode === key ? `0 4px 20px ${clr}26` : 'none', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: mode === key ? clr : (darkMode ? '#334155' : '#E2E8F0'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>{icon}</div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: mode === key ? clr : (darkMode ? '#94A3B8' : '#64748B') }}>{label}</div>
                <div style={{ fontSize: 12, color: darkMode ? '#64748B' : '#94A3B8', marginTop: 2 }}>Manage your {label.toLowerCase()}s</div>
              </div>
            </button>
          ))}
        </div>

        {/* ── CUSTOMER SECTION ── */}
        {mode === 'customer' && (
          <>
            <div style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}`, marginBottom: 18, display: 'flex', gap: 4 }}>
              {[['list','👥 All Customers'],['outstanding','💰 Outstanding']].map(([id, lbl]) => (
                <button key={id} onClick={() => setCustomerTab(id)} style={{ padding: '9px 20px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: 'transparent', borderBottom: customerTab === id ? `3px solid ${acc}` : '3px solid transparent', color: customerTab === id ? acc : (darkMode ? '#64748B' : '#94A3B8'), marginBottom: -2 }}>{lbl}</button>
              ))}
            </div>
            {customerTab === 'outstanding' && <OutstandingPanel partyType="customer" />}
            {customerTab === 'list' && (
              <>
                {/* Toolbar */}
                <div style={{ ...card, padding: '12px 14px', marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input style={{ ...inp, flex: 1, minWidth: 200 }} placeholder="Name, phone, email, address, GSTIN, PAN, state, tags…" value={custSearch} onChange={e => { setCustSearch(e.target.value); setCustPage(1); setSelectedIdx(-1); }} />
                  <select style={inp} value={custType} onChange={e => { setCustType(e.target.value); setCustPage(1); setSelectedIdx(-1); }}>
                    <option value="">All Types</option>
                    <option value="normal">👤 Normal</option>
                    <option value="business">🏢 Business</option>
                  </select>
                  <button onClick={() => setShowAddCust(true)} style={{ padding: '9px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>➕ Add Customer</button>
                  <button onClick={async () => { try { const r = await bulkAPI.exportCustomers(); const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'customers_export.xlsx'; a.click(); URL.revokeObjectURL(url); } catch { alert('Export failed'); } }} style={{ padding: '9px 12px', background: darkMode ? '#1E293B' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>📤 Export</button>
                  <label style={{ padding: '9px 12px', background: darkMode ? '#1E293B' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'inline-block', whiteSpace: 'nowrap' }}>
                    📥 Import
                    <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={async e => {
                      const f = e.target.files?.[0]; if (!f) return;
                      try {
                        const r = await bulkAPI.importCustomers(f);
                        const dups = r.data.duplicate_warnings?.length
                          ? `\n\nSkipped duplicates:\n${r.data.duplicate_warnings.slice(0,5).join('\n')}${r.data.duplicate_warnings.length > 5 ? `\n...+${r.data.duplicate_warnings.length - 5} more` : ''}`
                          : '';
                        alert(`✅ Imported: ${r.data.imported}, Skipped: ${r.data.skipped}${dups}`);
                        loadCustomers(); refreshCustomers(true);
                      } catch (err) { alert('Import failed: ' + (err.response?.data?.detail || err.message)); }
                      e.target.value = '';
                    }} />
                  </label>
                </div>

                {/* List */}
                <div className="stagger-list" style={{ ...card, overflow: 'hidden' }}>
                  {custLoading ? (
                    <div style={{ textAlign: 'center', padding: 60, ...muted }}>Loading customers…</div>
                  ) : customers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 60 }}>
                      <div style={{ fontSize: 56, marginBottom: 12 }}>👤</div>
                      <p style={muted}>No customers found</p>
                      <button onClick={() => setShowAddCust(true)} style={{ marginTop: 12, padding: '10px 24px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Add First Customer</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 580 }}>
                          <thead>
                            <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                              {['#','Customer','Type','Contact','GSTIN / PAN','Action'].map(h => (
                                <th key={h} style={{ padding: '9px 12px', textAlign: h === 'Action' ? 'right' : 'left', fontSize: 11, fontWeight: 700, ...muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {customers.map((c, i) => {
                              const isSel = selectedIdx === i;
                              return (
                                <tr key={c.id} data-idx={i}
                                  onClick={() => setSelectedIdx(i)}
                                  onDoubleClick={() => setDetailsParty({ party: c, partyType: 'customer' })}
                                  style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, background: isSel ? (darkMode ? `${acc}22` : `${acc}11`) : 'transparent', outline: isSel ? `2px solid ${acc}` : 'none', outlineOffset: -2, cursor: 'pointer' }}>
                                  <td style={{ padding: '10px 12px', fontSize: 12, ...muted, width: 36 }}>{(custPage - 1) * 15 + i + 1}</td>
                                  <td style={{ padding: '10px 12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span style={{ fontWeight: 700, ...pg }}>{c.name}</span>
                                      {isNew(c.created_at) && <span style={{ padding: '1px 6px', borderRadius: 10, fontSize: 9, fontWeight: 800, background: '#dcfce7', color: '#15803d' }}>NEW</span>}
                                    </div>
                                    {c.email && <div style={{ fontSize: 11, ...muted }}>{c.email}</div>}
                                    {parseFloat(c.outstanding) > 0 && <div style={{ marginTop: 2, display: 'inline-block', padding: '1px 6px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: '#fee2e2', color: '#991b1b' }}>₹{fmtNum(c.outstanding)} due</div>}
                                  </td>
                                  <td style={{ padding: '10px 12px' }}>{typeBadge(c.customer_type)}</td>
                                  <td style={{ padding: '10px 12px', fontSize: 12, ...muted }}>{c.mobile || '—'}</td>
                                  <td style={{ padding: '10px 12px', fontSize: 11 }}>
                                    {c.gstin && <div style={{ fontFamily: 'monospace', color: darkMode ? '#93c5fd' : '#1d4ed8' }}>G: {c.gstin}</div>}
                                    {c.pan && <div style={{ fontFamily: 'monospace', ...muted }}>P: {c.pan}</div>}
                                    {!c.gstin && !c.pan && <span style={muted}>—</span>}
                                  </td>
                                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                    <button onClick={ev => { ev.stopPropagation(); setDetailsParty({ party: c, partyType: 'customer' }); }}
                                      style={{ padding: '6px 14px', background: darkMode ? '#334155' : '#F1F5F9', color: acc, border: `1px solid ${acc}40`, borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                                      Details
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {custPages > 1 && (
                        <div style={{ padding: '10px 14px', borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <button disabled={custPage === 1} onClick={() => { setCustPage(p => p - 1); setSelectedIdx(-1); }} style={{ padding: '6px 14px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, background: 'transparent', ...pg, cursor: 'pointer' }}>&larr; Prev</button>
                          <span style={{ fontSize: 12, ...muted }}>Page {custPage} / {custPages} &mdash; {custTotal} total</span>
                          <button disabled={custPage === custPages} onClick={() => { setCustPage(p => p + 1); setSelectedIdx(-1); }} style={{ padding: '6px 14px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, background: 'transparent', ...pg, cursor: 'pointer' }}>Next &rarr;</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* ── SUPPLIER SECTION ── */}
        {mode === 'supplier' && (
          <>
            <div style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}`, marginBottom: 18, display: 'flex', gap: 4 }}>
              {[['list','🏭 All Suppliers'],['outstanding','💰 Outstanding']].map(([id, lbl]) => (
                <button key={id} onClick={() => setSupplierTab(id)} style={{ padding: '9px 20px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, background: 'transparent', borderBottom: supplierTab === id ? `3px solid ${acc}` : '3px solid transparent', color: supplierTab === id ? acc : (darkMode ? '#64748B' : '#94A3B8'), marginBottom: -2 }}>{lbl}</button>
              ))}
            </div>
            {supplierTab === 'outstanding' && <OutstandingPanel partyType="supplier" />}
            {supplierTab === 'list' && (
              <>
                <div style={{ ...card, padding: '12px 14px', marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input style={{ ...inp, flex: 1, minWidth: 200 }} placeholder="Name, phone, email, address, GSTIN, contact person…" value={supSearch} onChange={e => { setSupSearch(e.target.value); setSelectedIdx(-1); }} />
                  <button onClick={() => setShowAddSup(true)} style={{ padding: '9px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>➕ Add Supplier</button>
                  <button onClick={async () => { try { const r = await bulkAPI.exportSuppliers(); const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = 'suppliers_export.xlsx'; a.click(); URL.revokeObjectURL(url); } catch { alert('Export failed'); } }} style={{ padding: '9px 12px', background: darkMode ? '#1E293B' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>📤 Export</button>
                  <label style={{ padding: '9px 12px', background: darkMode ? '#1E293B' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'inline-block', whiteSpace: 'nowrap' }}>
                    📥 Import
                    <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={async e => {
                      const f = e.target.files?.[0]; if (!f) return;
                      try {
                        const r = await bulkAPI.importSuppliers(f);
                        const dups = r.data.duplicate_warnings?.length
                          ? `\n\nSkipped duplicates:\n${r.data.duplicate_warnings.slice(0,5).join('\n')}${r.data.duplicate_warnings.length > 5 ? `\n...+${r.data.duplicate_warnings.length - 5} more` : ''}`
                          : '';
                        alert(`✅ Imported: ${r.data.imported}, Skipped: ${r.data.skipped}${dups}`);
                        loadSuppliers(); refreshSuppliers(true);
                      } catch (err) { alert('Import failed: ' + (err.response?.data?.detail || err.message)); }
                      e.target.value = '';
                    }} />
                  </label>
                </div>

                <div className="stagger-list" style={{ ...card, overflow: 'hidden' }}>
                  {supLoading ? (
                    <div style={{ textAlign: 'center', padding: 60, ...muted }}>Loading suppliers…</div>
                  ) : suppliers.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 60 }}>
                      <div style={{ fontSize: 56, marginBottom: 12 }}>🏭</div>
                      <p style={muted}>No suppliers yet</p>
                      <button onClick={() => setShowAddSup(true)} style={{ marginTop: 12, padding: '10px 24px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Add First Supplier</button>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                        <thead>
                          <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                            {['#','Supplier','Phone','City / State','GSTIN','Status','Action'].map(h => (
                              <th key={h} style={{ padding: '9px 12px', textAlign: h === 'Action' ? 'right' : 'left', fontSize: 11, fontWeight: 700, ...muted, textTransform: 'uppercase' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {suppliers.map((s, i) => {
                            const isSel = selectedIdx === i;
                            return (
                              <tr key={s.id} data-idx={i}
                                onClick={() => setSelectedIdx(i)}
                                onDoubleClick={() => setDetailsParty({ party: s, partyType: 'supplier' })}
                                style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, background: isSel ? (darkMode ? `${acc}22` : `${acc}11`) : 'transparent', outline: isSel ? `2px solid ${acc}` : 'none', outlineOffset: -2, cursor: 'pointer' }}>
                                <td style={{ padding: '9px 12px', fontSize: 12, ...muted, width: 36 }}>{i + 1}</td>
                                <td style={{ padding: '9px 12px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontWeight: 700, fontSize: 14, ...pg }}>{s.name}</span>
                                    {isNew(s.created_at) && <span style={{ padding: '1px 6px', borderRadius: 10, fontSize: 9, fontWeight: 800, background: '#dcfce7', color: '#15803d' }}>NEW</span>}
                                  </div>
                                  {s.contact_person && <div style={{ fontSize: 12, ...muted }}>{s.contact_person}</div>}
                                  {parseFloat(s.outstanding) > 0 && <div style={{ marginTop: 2, display: 'inline-block', padding: '1px 6px', borderRadius: 5, fontSize: 10, fontWeight: 700, background: '#fee2e2', color: '#991b1b' }}>₹{fmtNum(s.outstanding)} due</div>}
                                </td>
                                <td style={{ padding: '9px 12px', fontSize: 13, ...muted }}>{s.phone || '—'}</td>
                                <td style={{ padding: '9px 12px', fontSize: 12, ...muted }}>{[s.city, s.state].filter(Boolean).join(', ') || '—'}</td>
                                <td style={{ padding: '9px 12px', fontSize: 11, fontFamily: 'monospace', ...muted }}>{s.gstin || '—'}</td>
                                <td style={{ padding: '9px 12px' }}>
                                  {s.status && <span style={{ padding: '2px 7px', borderRadius: 5, fontWeight: 700, fontSize: 10, background: s.status === 'active' ? '#dcfce7' : '#fef2f2', color: s.status === 'active' ? '#16a34a' : '#dc2626' }}>{s.status}</span>}
                                </td>
                                <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                                  <button onClick={ev => { ev.stopPropagation(); setDetailsParty({ party: s, partyType: 'supplier' }); }}
                                    style={{ padding: '6px 14px', background: darkMode ? '#334155' : '#F1F5F9', color: acc, border: `1px solid ${acc}40`, borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                                    Details
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showAddCust && (
        <AddCustomerModal onClose={() => {
            setShowAddCust(false);
            if (sourceRoute.current) { const dest = sourceRoute.current; sourceRoute.current = null; navigate(dest, { replace: true }); }
          }}
          onSaved={() => { setShowAddCust(false); loadCustomers(); refreshCustomers(true); refreshStats(); if (sourceRoute.current) { const dest = sourceRoute.current; sourceRoute.current = null; navigate(dest, { replace: true }); } }} />
      )}
      {showAddSup && (
        <AddSupplierModal onClose={() => {
            setShowAddSup(false);
            if (sourceRoute.current) { const dest = sourceRoute.current; sourceRoute.current = null; navigate(dest, { replace: true }); }
          }}
          onSaved={() => { setShowAddSup(false); loadSuppliers(); refreshSuppliers(true); if (sourceRoute.current) { const dest = sourceRoute.current; sourceRoute.current = null; navigate(dest, { replace: true }); } }} />
      )}
      {detailsParty && (
        <DetailsModal
          party={detailsParty.party}
          partyType={detailsParty.partyType}
          onClose={() => setDetailsParty(null)}
          onSaved={() => {
            setDetailsParty(null);
            if (detailsParty.partyType === 'customer') { loadCustomers(); refreshCustomers(true); refreshStats(); }
            else { loadSuppliers(); refreshSuppliers(true); }
          }}
          onDeleted={() => {
            setDetailsParty(null);
            if (detailsParty.partyType === 'customer') { loadCustomers(); refreshCustomers(true); refreshStats(); }
            else { loadSuppliers(); refreshSuppliers(true); }
          }}
        />
      )}
    </Layout>
  );
};

export default Parties;
